// Componente compartilhado: input de convite com autocomplete de usuários.
//
// Uma ÚNICA implementação reusada em todas as telas de convite (modal de
// membros, modal de parceiros, etc). Genérico — não conhece domínio de trip.
// A lógica de gerar o invite fica no caller, passada via callback `onInvite`.
//
// Comportamento:
//   - O usuário digita um NOME ou EMAIL. Conforme digita, busca usuários
//     Mapiare existentes (GET /api/users/search, debounced) e mostra um
//     dropdown de resultados.
//   - Clicar num resultado seleciona aquele usuário e usa o email dele.
//   - Se o valor digitado não for um usuário existente, basta apertar Enter
//     num email válido pra convidar esse endereço mesmo assim.
//   - Email inválido (e sem usuário selecionado) → erro inline; não passa.
//   - O convite é SEMPRE amarrado a um email.
//
// API:
//   const ac = createUserInviteAutocomplete({
//     onInvite: async ({ email, uid, displayName }) => { ... },
//     placeholder?: string,
//     disabled?: boolean,
//     excludeEmails?: string[],   // emails que não podem ser convidados de novo
//     onError?: (msg) => void,    // erro custom além do inline (opcional)
//     searchUsers?: (term) => Promise<{ users: [...] }>,
//                                 // override da busca (default: GET /api/users/search).
//                                 // Útil pra testes; em produção não passar.
//   });
//   container.appendChild(ac.element);
//   ac.focus();        // foca o input
//   ac.clear();        // limpa input + seleção + erro
//   ac.destroy();      // remove listeners globais
//
// `onInvite` pode lançar — o componente mostra a mensagem do erro inline e
// reabilita o input. Se resolver, o componente limpa o input automaticamente.

import { authFetch } from '../../core/auth.js';
import { API } from '../../core/api.js';

const DEBOUNCE_MS = 220;
const MIN_QUERY_LEN = 2;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

let _idSeq = 0;

export function isValidEmail(s) {
  return EMAIL_RE.test(String(s || '').trim());
}

function esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Mascara um email pra exibição (LGPD): mantém as 2 primeiras letras do local
// e o domínio, escondendo o resto. "gabriel@gmail.com" → "ga•••@gmail.com".
// O email REAL nunca vai pra tela — só é usado internamente pra gerar o convite.
function maskEmail(email) {
  const s = String(email || '').trim();
  const at = s.indexOf('@');
  if (at < 1) return s ? '•••' : '';
  const local = s.slice(0, at);
  const domain = s.slice(at); // inclui o "@"
  const keep = local.length <= 2 ? 1 : 2;
  return local.slice(0, keep) + '•••' + domain;
}

// Iniciais a partir de nome ou email — fallback de avatar quando não há foto.
function initials(nameOrEmail) {
  const s = String(nameOrEmail || '').trim();
  if (!s) return '?';
  const local = s.includes('@') ? s.split('@')[0] : s;
  const parts = local.replace(/[._-]+/g, ' ').split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Busca padrão: GET /api/users/search autenticado.
async function defaultSearchUsers(term) {
  const res = await authFetch(API.usersSearch(term));
  if (!res.ok) throw new Error(`busca falhou (${res.status})`);
  return res.json();
}

export function createUserInviteAutocomplete({
  onInvite,
  placeholder = 'Nome ou email da pessoa',
  disabled = false,
  excludeEmails = [],
  onError,
  searchUsers = defaultSearchUsers,
} = {}) {
  const uid = `uia-${++_idSeq}`;
  const excluded = new Set((excludeEmails || []).map((e) => String(e).toLowerCase()));

  // ── DOM ────────────────────────────────────────────────────────────
  const root = document.createElement('div');
  root.className = 'user-invite-ac';
  root.innerHTML = `
    <div class="user-invite-ac-row">
      <div class="user-invite-ac-field">
        <input type="text" class="input user-invite-ac-input"
          id="${uid}-input" role="combobox" aria-autocomplete="list"
          aria-expanded="false" aria-controls="${uid}-list"
          autocomplete="off" spellcheck="false" enterkeyhint="done"
          placeholder="${esc(placeholder)}"/>
        <ul class="user-invite-ac-menu" id="${uid}-list" role="listbox" hidden></ul>
      </div>
      <button type="button" class="user-invite-ac-submit" data-action="submit"
        aria-label="Convidar">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="1.9" stroke-linecap="round"
          stroke-linejoin="round" aria-hidden="true">
          <path d="M9 10l-4 4 4 4M5 14h12a4 4 0 0 0 4-4V5"/>
        </svg>
      </button>
    </div>
    <p class="user-invite-ac-error" data-slot="error" role="alert"></p>
  `;

  const input = root.querySelector('.user-invite-ac-input');
  const menu = root.querySelector('.user-invite-ac-menu');
  const submitBtn = root.querySelector('[data-action="submit"]');
  const errorEl = root.querySelector('[data-slot="error"]');

  if (disabled) setDisabled(true);

  // ── estado ─────────────────────────────────────────────────────────
  let results = [];          // [{ uid, email, displayName, photoURL }]
  let activeIndex = -1;       // item destacado no dropdown
  let selectedUser = null;    // usuário escolhido por clique
  let debounceTimer = null;
  let reqSeq = 0;            // descarta respostas fora de ordem
  let busy = false;
  // Termo da última busca que CONCLUIU com sucesso e zero resultados —
  // só nesse caso mostramos o aviso "nenhum usuário encontrado". Falha de
  // rede/auth NÃO seta isso (degrada silenciosamente pra input de texto).
  let emptyTerm = '';

  // ── helpers ────────────────────────────────────────────────────────
  function setError(msg) {
    errorEl.textContent = msg || '';
    input.classList.toggle('input--invalid', !!msg);
    if (msg) onError?.(msg);
  }

  function setDisabled(v) {
    input.disabled = v;
    submitBtn.disabled = v;
    root.classList.toggle('is-disabled', v);
  }

  function closeMenu() {
    menu.hidden = true;
    menu.innerHTML = '';
    activeIndex = -1;
    emptyTerm = '';
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
  }

  function renderMenu() {
    if (!results.length) {
      // Sem resultados: se a busca CONCLUIU vazia, mostra um aviso
      // informativo (não bloqueia — dá pra convidar por email mesmo assim).
      if (emptyTerm) { renderEmptyNotice(); return; }
      closeMenu();
      return;
    }
    menu.innerHTML = results.map((u, i) => {
      // LGPD: nunca expor o email de outra pessoa no dropdown. Mostra só o
      // nome; quem não tem nome cadastrado aparece com email mascarado.
      const name = u.displayName || maskEmail(u.email);
      const sub = '';
      const avatar = u.photoURL
        ? `<img class="user-invite-ac-avatar" src="${esc(u.photoURL)}" alt=""/>`
        : `<span class="user-invite-ac-avatar user-invite-ac-avatar--initials">${esc(initials(name))}</span>`;
      return `
        <li class="user-invite-ac-option${i === activeIndex ? ' is-active' : ''}"
          id="${uid}-opt-${i}" role="option" data-index="${i}"
          aria-selected="${i === activeIndex}">
          ${avatar}
          <span class="user-invite-ac-option-text">
            <span class="user-invite-ac-option-name">${esc(name)}</span>
            ${sub ? `<span class="user-invite-ac-option-sub">${esc(sub)}</span>` : ''}
          </span>
        </li>
      `;
    }).join('');
    menu.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    if (activeIndex >= 0) {
      input.setAttribute('aria-activedescendant', `${uid}-opt-${activeIndex}`);
    } else {
      input.removeAttribute('aria-activedescendant');
    }
  }

  // Aviso informativo de "nenhum usuário Mapiare encontrado". Renderizado no
  // próprio dropdown, mas NÃO é uma `option` (sem role="option", sem
  // data-index) — não dá pra selecionar nem destacar com as setas, e o Enter
  // continua caindo no doInvite() (convidar o email digitado).
  function renderEmptyNotice() {
    const isEmail = isValidEmail(input.value.trim());
    const hint = isEmail
      ? 'Você ainda pode convidar essa pessoa pelo email — é só apertar Enter.'
      : 'Digite um email completo para convidar mesmo assim.';
    menu.innerHTML = `
      <li class="user-invite-ac-empty" role="status" aria-live="polite">
        <span class="user-invite-ac-empty-title">Nenhum usuário do Mapiare encontrado</span>
        <span class="user-invite-ac-empty-hint">${esc(hint)}</span>
      </li>
    `;
    menu.hidden = false;
    activeIndex = -1;
    // aria-expanded fica false: não há opções selecionáveis no dropdown.
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
  }

  async function runSearch(term) {
    const seq = ++reqSeq;
    try {
      const data = await searchUsers(term);
      if (seq !== reqSeq) return;            // resposta obsoleta — ignora
      // Filtra quem já é membro/convidado — não faz sentido sugerir.
      results = (data?.users || []).filter(
        (u) => !excluded.has(String(u.email || '').toLowerCase()),
      );
      activeIndex = -1;
      // Busca concluiu: se voltou vazia, lembra o termo pra mostrar o aviso.
      emptyTerm = results.length ? '' : term;
      renderMenu();
    } catch {
      // Rede / backend fora: degrada pra input de texto puro (sem dropdown).
      // NÃO mostra o aviso de "não encontrado" — não dá pra afirmar isso.
      if (seq === reqSeq) { results = []; emptyTerm = ''; closeMenu(); }
    }
  }

  function scheduleSearch() {
    clearTimeout(debounceTimer);
    const term = input.value.trim();
    selectedUser = null;                     // digitar invalida a seleção
    emptyTerm = '';                          // termo mudou — aviso fica obsoleto
    setError('');
    if (term.length < MIN_QUERY_LEN) { results = []; closeMenu(); return; }
    debounceTimer = setTimeout(() => runSearch(term), DEBOUNCE_MS);
  }

  function pickResult(idx) {
    const u = results[idx];
    if (!u) return;
    selectedUser = u;
    // LGPD: não colocar o email real no input visível. O email real fica só
    // em selectedUser (usado internamente pra amarrar o convite).
    input.value = u.displayName || maskEmail(u.email);
    setError('');
    closeMenu();
    input.focus();
  }

  // Resolve o email a convidar a partir do estado atual.
  // Retorna { email, uid?, displayName? } ou lança Error com msg amigável.
  function resolveTarget() {
    if (selectedUser) {
      return {
        email: selectedUser.email,
        uid: selectedUser.uid,
        displayName: selectedUser.displayName || null,
      };
    }
    const raw = input.value.trim();
    if (!raw) throw new Error('Digite um nome ou email.');
    if (!isValidEmail(raw)) {
      throw new Error('Digite um email válido ou escolha alguém da lista.');
    }
    return { email: raw.toLowerCase(), uid: null, displayName: null };
  }

  async function doInvite() {
    if (busy) return;
    let target;
    try {
      target = resolveTarget();
    } catch (e) {
      setError(e.message);
      return;
    }
    if (excluded.has(target.email.toLowerCase())) {
      setError('Essa pessoa já foi convidada ou já é membro.');
      return;
    }

    busy = true;
    setDisabled(true);
    setError('');
    try {
      await onInvite?.(target);
      // Sucesso — limpa pra próximo convite.
      input.value = '';
      selectedUser = null;
      results = [];
      closeMenu();
    } catch (e) {
      setError(e?.message || 'Falha ao gerar convite.');
    } finally {
      busy = false;
      setDisabled(false);
      input.focus();
    }
  }

  // ── eventos ────────────────────────────────────────────────────────
  input.addEventListener('input', scheduleSearch);

  input.addEventListener('keydown', (e) => {
    const open = !menu.hidden && results.length > 0;
    if (e.key === 'ArrowDown' && open) {
      e.preventDefault();
      activeIndex = (activeIndex + 1) % results.length;
      renderMenu();
    } else if (e.key === 'ArrowUp' && open) {
      e.preventDefault();
      activeIndex = (activeIndex - 1 + results.length) % results.length;
      renderMenu();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && activeIndex >= 0) {
        pickResult(activeIndex);
      } else {
        // Enter sem item destacado → tenta convidar o que estiver no input.
        doInvite();
      }
    } else if (e.key === 'Escape' && !menu.hidden) {
      // Fecha tanto a lista de resultados quanto o aviso de "não encontrado".
      e.preventDefault();
      closeMenu();
    }
  });

  // Clique numa opção do dropdown.
  menu.addEventListener('mousedown', (e) => {
    // mousedown (não click) pra disparar antes do blur do input.
    const li = e.target.closest('.user-invite-ac-option');
    if (!li) return;
    e.preventDefault();
    pickResult(Number(li.dataset.index));
  });

  submitBtn.addEventListener('click', (e) => {
    e.preventDefault();
    doInvite();
  });

  // Fecha o dropdown ao clicar fora do componente.
  function onDocPointerDown(e) {
    if (!root.contains(e.target)) closeMenu();
  }
  document.addEventListener('pointerdown', onDocPointerDown);

  // ── API pública ────────────────────────────────────────────────────
  return {
    element: root,
    focus() { input.focus(); },
    clear() {
      input.value = '';
      selectedUser = null;
      results = [];
      setError('');
      closeMenu();
    },
    setDisabled,
    destroy() {
      clearTimeout(debounceTimer);
      document.removeEventListener('pointerdown', onDocPointerDown);
    },
  };
}
